import { requireCompletedProfile } from "@hisani/auth";
import { canRequestStableTransfer } from "../domain/membership/authorization-policy.js";
import { canInitiateStableTransfer } from "../domain/horses/horse-lifecycle.js";
import { normalizeStablePublicId } from "../domain/stable/public-id.js";
import { cancelStableTransferSchema, listIncomingStableTransferRequestsSchema, listOutgoingStableTransferRequestsSchema, requestStableTransferSchema, respondStableTransferSchema, stableCapacityLimit, stableConflict, stableForbidden, stableInvalidTransition, stableNotFound, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
const FEATURE = "STABLE_TRANSFER";
/**
 * Initiates a Stable Transfer request (`docs/products/stable.md` "Stable
 * Transfer", steps 1-5). Restricted to Trainer/Co-Trainer/Assistant Trainer
 * (`STABLE_TRANSFER` FULL access) at a Stable holding the paid subscription
 * tier — mirrors `requestOwnerLink`'s shape closely.
 */
export async function requestStableTransfer(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = requestStableTransferSchema.parse(input);
    const membership = await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const normalizedDestinationPublicId = normalizeStablePublicId(parsed.destinationPublicId);
    const selectedHorsemanIds = [
        ...new Set(parsed.ownerLinkHorsemanIdsToCarryForward),
    ].sort();
    const commandFingerprint = dependencies.identifierFingerprint.fingerprint([
        "STABLE_TRANSFER_REQUEST",
        parsed.stableId,
        parsed.horseId,
        normalizedDestinationPublicId,
        ...selectedHorsemanIds,
    ].join("\u001f"));
    const replay = await dependencies.repository.replayStableTransferRequest({
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint,
    });
    if (replay?.outcome === "REPLAYED")
        return replay.request;
    if (replay?.outcome === "ALREADY_ACTIVE") {
        throw stableConflict("This horse already has an active transfer request.");
    }
    if (replay?.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Stable Transfer command.");
    }
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (dashboard === null)
        throw stableNotFound();
    if (!canRequestStableTransfer(membership, dashboard.stable.subscriptionTier)) {
        throw stableForbidden("Stable Transfer requires the initiating Stable's paid subscription.");
    }
    const horse = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (horse === null)
        throw stableNotFound();
    if (!canInitiateStableTransfer(horse)) {
        throw stableInvalidTransition("This horse is not currently transferable (it is Deceased, or already inactive/in another transfer).");
    }
    const destination = await dependencies.repository.findStableByPublicId(normalizedDestinationPublicId);
    if (destination === null)
        throw stableNotFound();
    if (destination.id === parsed.stableId) {
        throw stableConflict("A Stable cannot transfer a horse to itself.");
    }
    if (selectedHorsemanIds.length > 0) {
        const acceptedLinks = await dependencies.repository.listHorseOwnerLinks(parsed.stableId, parsed.horseId);
        const acceptedHorsemanIds = new Set(acceptedLinks.map((link) => link.horsemanId));
        for (const horsemanId of selectedHorsemanIds) {
            if (!acceptedHorsemanIds.has(horsemanId)) {
                throw stableConflict("An Owner Link selected to carry forward must be a currently accepted owner of this horse.");
            }
        }
    }
    const now = dependencies.clock.now();
    const policy = await dependencies.policy.get();
    const result = await dependencies.repository.requestStableTransfer({
        id: dependencies.newId(),
        originStableId: parsed.stableId,
        destinationStableId: destination.id,
        horseId: parsed.horseId,
        requestedByHorsemanId: actor.account.id,
        ownerLinkHorsemanIdsToCarryForward: selectedHorsemanIds,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint,
        now,
        expiresAt: new Date(now.getTime() +
            policy.stableTransferRequestExpiryDays * 24 * 60 * 60 * 1000),
    });
    if (result.outcome === "ALREADY_ACTIVE") {
        throw stableConflict("This horse already has an active transfer request.");
    }
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Stable Transfer command.");
    }
    return result.request;
}
export async function listOutgoingStableTransferRequests(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listOutgoingStableTransferRequestsSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.listOutgoingStableTransferRequests(parsed.stableId);
}
export async function listIncomingStableTransferRequests(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listIncomingStableTransferRequestsSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    return dependencies.repository.listIncomingStableTransferRequests(parsed.stableId);
}
/** Origin-side cancellation of a still-PENDING outgoing request. */
export async function cancelStableTransfer(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = cancelStableTransferSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const now = dependencies.clock.now();
    const outcome = await dependencies.repository.cancelStableTransferRequest({
        originStableId: parsed.stableId,
        transferId: parsed.transferId,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint([
            "STABLE_TRANSFER_CANCEL",
            parsed.stableId,
            parsed.transferId,
            parsed.expectedVersion,
        ].join("\u001f")),
        expectedVersion: parsed.expectedVersion,
        now,
    });
    if (outcome.outcome === "NOT_FOUND")
        throw stableNotFound();
    if (outcome.outcome === "STALE_VERSION") {
        throw stableConflict("This Stable Transfer request has changed. Refresh and retry.");
    }
    if (outcome.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Stable Transfer command.");
    }
    if (!("request" in outcome)) {
        throw stableConflict("The Stable Transfer request could not be cancelled.");
    }
    return { cancelled: true, request: outcome.request };
}
/**
 * Destination-side accept/decline (`docs/products/stable.md` "Stable
 * Transfer", acceptance steps 1-6). Authorization ambiguity resolution: the
 * task brief's `COMPANY.md` Core Entities summary says "requiring the
 * destination Stable Owner's explicit acceptance", but `docs/products/
 * stable.md`'s dedicated Stable Transfer section is more detailed —
 * "destination Stable Owner, or Trainer/Co-Trainer/Assistant Trainer per
 * the destination's own permission matrix" — i.e. the ordinary
 * `STABLE_TRANSFER` feature check on the destination Stable (the same
 * `requireFeatureMembership` gate every other Sealed/boundary-crossing
 * feature uses), not an Entity-Owner-tag-specific check. This function
 * follows the more detailed wording, per the task brief's own instruction
 * to prefer it when genuinely ambiguous.
 */
export async function respondStableTransfer(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = respondStableTransferSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const now = dependencies.clock.now();
    const policy = await dependencies.policy.get();
    const result = await dependencies.repository.respondStableTransfer({
        destinationStableId: parsed.stableId,
        transferId: parsed.transferId,
        actorId: actor.account.id,
        decision: parsed.decision,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint([
            "STABLE_TRANSFER_RESPOND",
            parsed.stableId,
            parsed.transferId,
            parsed.decision,
            parsed.expectedVersion,
        ].join("\u001f")),
        expectedVersion: parsed.expectedVersion,
        now,
        newDestinationHorseId: dependencies.newId(),
        capacityPolicy: policy,
        gracePeriodDays: policy.stableTransferGracePeriodDays,
    });
    if (result.outcome === "NOT_FOUND") {
        throw stableConflict("This transfer request is no longer pending, expired, or does not belong to this Stable.");
    }
    if (result.outcome === "CAPACITY_LIMIT") {
        throw stableCapacityLimit();
    }
    if (result.outcome === "STALE_VERSION") {
        throw stableConflict("This Stable Transfer request has changed. Refresh and retry.");
    }
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Stable Transfer command.");
    }
    if (!("request" in result)) {
        throw stableConflict("The Stable Transfer response could not be recorded.");
    }
    return result.request;
}
